import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";
import PortugalSpainQuickFitCheck from "@/components/PortugalSpainQuickFitCheck";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const sections = [
  {
    title: "Why do remote workers and freelancers need a different country filter?",
    paragraphs: [
      "A country can look appealing on climate or cost and still be a weak fit if internet reliability, visa friction, time-zone constraints, or workday energy do not hold up in real life.",
      "That is why remote-worker decisions often separate on infrastructure, flexibility, and whether the country supports the pace and rhythm of how you actually work.",
    ],
  },
  {
    title: "What usually matters most for sustainable remote work abroad?",
    bullets: [
      "stable internet and dependable day-to-day infrastructure",
      "a cost structure that does not undermine work freedom",
      "time zones and travel access that support clients or team collaboration",
      "visa and stay rules that do not create constant background friction",
    ],
  },
  {
    title: "Why can the same country feel great for one remote worker and wrong for another?",
    paragraphs: [
      "Because remote work is not one lifestyle. Some people want slower days, lower costs, and a softer landing. Others want stronger coworking communities, bigger city energy, or easier coordination with North American or European work schedules.",
      "The stronger answer depends on what your work week needs from the place—not just what looks attractive on a map.",
    ],
  },
  {
    title: "What should a useful remote-work comparison surface?",
    bullets: [
      "where work logistics feel lighter rather than heavier",
      "which countries still look sustainable once business costs and daily living costs are combined",
      "what tradeoffs you may be underestimating before you commit deeper research time",
    ],
  },
];

const REMOTE_QUICK_FIT_QUESTIONS = [
  {
    id: "internet_vs_cost",
    question: "What matters more right now?",
    options: [
      { label: "Excellent internet and smoother infrastructure", scores: { ES: 2, PT: 2, DE: 1 } },
      { label: "Lower cost of living and more financial runway", scores: { MX: 2, PT: 1, TH: 1 } },
    ],
  },
  {
    id: "time_zone_preference",
    question: "What work schedule feels easier to support?",
    options: [
      { label: "Closer alignment with Europe or a blended schedule", scores: { PT: 2, ES: 2, DE: 1 } },
      { label: "More flexibility for North America or asynchronous work", scores: { MX: 2, CR: 1, TH: 1 } },
    ],
  },
  {
    id: "visa_flexibility",
    question: "How important is visa flexibility?",
    options: [
      { label: "Very important", scores: { PT: 2, MX: 1, CR: 1 } },
      { label: "I can handle more complexity if the work lifestyle is strong", scores: { ES: 1, DE: 1, TH: 1 } },
    ],
  },
  {
    id: "community_shape",
    question: "What kind of remote-work environment sounds better?",
    options: [
      { label: "A strong expat / coworking community", scores: { PT: 2, MX: 1, TH: 1 } },
      { label: "A broader local-city environment with more structure", scores: { ES: 2, DE: 1 } },
    ],
  },
  {
    id: "lifestyle_pace",
    question: "What daily pace feels more sustainable?",
    options: [
      { label: "Slower pace with lower pressure", scores: { PT: 2, MX: 1, CR: 1, TH: 1 } },
      { label: "A more active rhythm with stronger urban options", scores: { ES: 2, DE: 1 } },
    ],
  },
];

const REMOTE_COUNTRY_LABELS = {
  PT: "Portugal",
  ES: "Spain",
  MX: "Mexico",
  TH: "Thailand",
  CR: "Costa Rica",
  DE: "Germany",
};

const REMOTE_COUNTRY_EXPLANATIONS = {
  PT: "Portugal often rises when internet reliability, community, daily ease, and a softer landing all matter at the same time for remote work.",
  ES: "Spain often rises when stronger urban infrastructure, lifestyle variety, and a more active city rhythm matter more than pure cost efficiency.",
  MX: "Mexico often rises when cost flexibility, proximity to North America, and a more adaptable remote-work lifestyle carry the most weight.",
  TH: "Thailand often rises when lower cost of living and work-life freedom matter more than staying close to Western infrastructure norms.",
  CR: "Costa Rica often rises when climate, pace, and quality of life matter more than maximizing scale or big-city work energy.",
  DE: "Germany often rises when structure, infrastructure, and predictability matter more than keeping the move simple or low-cost.",
};

const REMOTE_FAQ_ITEMS = [
  {
    question: "Should remote workers choose a country mostly on cost of living?",
    answer:
      "Usually no. Lower cost helps, but a cheaper country can still be a weaker fit if work logistics, time zones, visa stress, or infrastructure keep adding friction to your week.",
  },
  {
    question: "Why do Portugal, Spain, Mexico, and Thailand all stay in the conversation?",
    answer:
      "Because they solve different versions of the remote-work problem. Some are stronger on infrastructure. Some are easier on budget. Some feel easier socially or geographically. The right answer depends on which of those advantages matters most for the way you work.",
  },
  {
    question: "How important is remote-work community compared with visa practicality?",
    answer:
      "Both matter, but their weight depends on your situation. Community can make daily life and work rhythm easier, while visa practicality determines whether the move stays stable enough to build on. A strong answer usually needs both to be at least workable.",
  },
  {
    question: "What should I do if the Quick Fit™ lead and my intuition differ?",
    answer:
      "Treat that as a useful signal rather than a problem. It usually means one or two priorities may be pulling harder than you expected, and the full assessment is the better next step for understanding the tradeoffs more completely.",
  },
];

const buildRemoteQuickFitResult = ({ lead, closeAlternativeNames = [] }) => ({
  headline: `${lead.name} may deserve the first closer look for remote work`,
  body: REMOTE_COUNTRY_EXPLANATIONS[lead.code] || `${lead.name} appears to align most closely with the remote-work priorities you selected.`,
  supportingNote: closeAlternativeNames.length
    ? closeAlternativeNames.length === 1
      ? `${closeAlternativeNames[0]} also came through as a strong match from the priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`
      : `${closeAlternativeNames.slice(0, -1).join(", ")} and ${closeAlternativeNames[closeAlternativeNames.length - 1]} also came through as strong matches from the priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`
    : "This is a directional result, not a final recommendation. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.",
});

export default function BestCountriesForRemoteWorkersAndFreelancers() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="best-countries-remote-freelancers-page">
      <SeoArticleSchema
        headline="Best Countries for Remote Workers and Freelancers"
        description="See how countries compare for remote workers and freelancers across infrastructure, affordability, visa flexibility, and everyday work-life fit."
        pathname="/insights/best-countries-for-remote-workers-and-freelancers"
        keywords={[
          "best countries for remote workers and freelancers",
          "best countries for digital nomads and freelancers",
          "remote work abroad country comparison",
        ]}
      />
      <Header />
      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="best-countries-remote-freelancers-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" />
            <p className="rc-section-kicker mb-4">Relocation insight</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="best-countries-remote-freelancers-page-title">Best Countries for Remote Workers and Freelancers</h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>The strongest countries for remote workers and freelancers usually separate on infrastructure, cost, visa flexibility, and whether the day-to-day work rhythm feels sustainable.</p>
              <p>That is why the better answer depends less on hype and more on what your work week actually needs from the place you live.</p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="best-countries-remote-freelancers-key-takeaway"
            paragraphs={[
              "There is no single best country for remote workers and freelancers. The stronger fit depends on how infrastructure, cost of living, visa flexibility, community, and lifestyle pace come together for the way you actually work.",
              "Countries that appeal to remote workers do so for different reasons. The useful question is not which destination is trendiest, but which one still feels sustainable when the workday becomes real.",
            ]}
          />

          <PortugalSpainQuickFitCheck
            rootTestId="best-countries-remote-freelancers-quick-fit"
            quickFitType="remote_workers_freelancers"
            questions={REMOTE_QUICK_FIT_QUESTIONS}
            countryLabels={REMOTE_COUNTRY_LABELS}
            countryPriorityOrder={["PT", "ES", "MX", "TH", "CR", "DE"]}
            buildResult={buildRemoteQuickFitResult}
          />

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-countries-remote-freelancers-faq-section">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="best-countries-remote-freelancers-faq-kicker">Questions people often still have</p>
              <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="best-countries-remote-freelancers-faq-title">Still Weighing the Best Remote-Work Options?</h2>
              <p className="mt-4 text-base leading-8 text-stone-600" data-testid="best-countries-remote-freelancers-faq-description">
                These are the questions that often remain once a few countries start looking strong for your work lifestyle but before the move feels clear enough to pursue.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-7 space-y-3" data-testid="best-countries-remote-freelancers-faq-accordion">
              {REMOTE_FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={item.question} value={`remote-faq-${index + 1}`} className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5" data-testid={`best-countries-remote-freelancers-faq-item-${index + 1}`}>
                  <AccordionTrigger className="py-5 text-left font-manrope text-lg text-stone-900 no-underline hover:no-underline" data-testid={`best-countries-remote-freelancers-faq-trigger-${index + 1}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="pb-5 text-sm leading-7 text-stone-600" data-testid={`best-countries-remote-freelancers-faq-answer-${index + 1}`}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="best-countries-remote-freelancers-sections-grid">
            {sections.map((section, index) => (
              <article key={section.title} className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid={`best-countries-remote-freelancers-section-${index + 1}`}>
                <h2 className="font-manrope text-2xl text-stone-900">{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {section.bullets?.length ? (
                  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" /><span>{bullet}</span></li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="best-countries-remote-freelancers-followup-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="best-countries-remote-freelancers-followup-title">How should you use this direction in practice?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="best-countries-remote-freelancers-followup-copy">
              <p>
                If you are still deciding what should lead the comparison, begin with{" "}
                <Link to="/insights/what-country-should-i-move-to" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-remote-freelancers-what-country-link">
                  the broader country-fit framework
                </Link>
                . If you already know which tradeoffs matter most, move into{" "}
                <Link to="/insights/create-country-shortlist-for-moving-abroad" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-remote-freelancers-shortlist-link">
                  a realistic shortlist
                </Link>
                .
              </p>
              <p>
                If affordability is still pulling too much attention by itself, it also helps to read{" "}
                <Link to="/insights/how-much-money-do-you-need-to-live-abroad" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="best-countries-remote-freelancers-budget-link">
                  how realistic budgets abroad actually work
                </Link>
                , because the better remote-work answer usually depends on what kind of work week that budget is meant to support.
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Continue exploring"
            title="If you are not ready for the full assessment yet, these are the most useful next pages from here."
            description="This topic usually gets clearer when you narrow the shortlist, test a real country comparison, and then preview how the Blueprint organizes the final decision before you personalize it."
            testIdPrefix="best-countries-remote-freelancers-next-steps"
            items={[
              {
                slug: "shortlist",
                eyebrow: "Narrow the field",
                title: "Build a realistic shortlist",
                description: "Separate broad interest from the countries that still make sense once your actual priorities are doing the sorting.",
                ctaLabel: "Open the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "comparison",
                eyebrow: "See a comparison",
                title: "See a country tradeoff example",
                description: "Watch how a real comparison becomes clearer once lifestyle, cost, healthcare, and practicality are weighed together.",
                ctaLabel: "View the comparison",
                to: "/insights/portugal-vs-spain-living-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the Blueprint",
                title: "Preview a Sample Blueprint",
                description: "See how the fuller paid experience organizes the shortlist, tradeoffs, and next steps before you commit.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}